import React, { useEffect, useRef, useState } from "react";
import {
  Activity,
  ArrowRight,
  Brain,
  CheckCircle2,
  Cpu,
  Eye,
  FileAudio,
  FileImage,
  FileText,
  Fingerprint,
  Folder,
  Info,
  Loader2,
  Microscope,
  MousePointer2,
  Play,
  RotateCcw,
  Upload,
  X,
  Zap,
} from "lucide-react";

const TEAL_DARK = "#085041";
const TEAL_MID = "#0F6E56";
const TEAL_LIGHT = "#5DCAA5";
const TEAL_TINT = "#EAF6F1";
const RULE = "#EDEFEE";
const SOFT = "#9CA3AF";
const WARNING_BG = "#FFFBEB";
const WARNING = "#D97706";
const SUCCESS_BG = "#F0FDF4";

const CARD_HOVER_MS = 650;
const CARD_CLICK_MS = 420;
const FILE_DIALOG_MS = 900;
const FILE_SELECT_MS = 700;
const UPLOAD_TICK_MS = 80;
const VALIDATION_MS = 700;
const READY_PAUSE_MS = 400;
const PIPELINE_STEP_MS = 1000;

const MODALITIES = [
  {
    key: "radiology",
    label: "Radiology Data (X-Ray/CT)",
    icon: FileImage,
    folder: "Chest X-rays",
    description: "Chest X-ray and CT imaging",
    files: ["tb_case_001.png", "tb_case_002.png", "normal_case_003.png", "pneumonia_case_001.png"],
    selectedFile: "tb_case_002.png",
    size: "4.8 MB",
  },
  {
    key: "acoustic",
    label: "Acoustic Data (Cough/Breath)",
    icon: FileAudio,
    folder: "Respiratory Audio",
    description: "Cough and breath sound recordings",
    files: ["healthy_breath.wav", "copd_cough.wav", "tb_cough_sample.wav", "pneumonia_cough.wav"],
    selectedFile: "tb_cough_sample.wav",
    size: "2.4 MB",
  },
  {
    key: "clinical",
    label: "Clinical Data (Text Notes)",
    icon: FileText,
    folder: "Clinical Records",
    description: "Clinical notes and laboratory summary",
    files: ["patient_notes_001.txt", "ehr_patient_002.txt", "lab_results_003.txt", "radiology_report_004.txt"],
    selectedFile: "patient_notes_001.txt",
    size: "82 KB",
  },
  {
    key: "pathology",
    label: "Pathology Data (Tissue Slide)",
    icon: Microscope,
    folder: "Pathology Slides",
    description: "Histopathology and biopsy images",
    files: ["normal_slide_001.png", "granuloma_slide_002.png", "tb_slide_003.png", "fungal_slide_004.png"],
    selectedFile: "tb_slide_003.png",
    size: "8.6 MB",
  },
];

const PIPELINE_STEPS = [
  { key: "preprocess", label: "Validate and preprocess inputs", detail: "Quality checks, normalization and modality-specific preparation", icon: Activity },
  { key: "encode", label: "Generate modality embeddings", detail: "Foundation encoders extract acoustic, imaging and clinical features", icon: Eye },
  { key: "project", label: "Project into shared concept space", detail: "Modality embeddings align to a common 128D representation", icon: ArrowRight },
  { key: "ontology", label: "Apply ontology-guided mapping", detail: "Concepts are linked through SNOMED CT, RadLex and UMLS", icon: Fingerprint },
  { key: "attention", label: "Calculate attention and quality weights", detail: "The system evaluates relevance and reliability by modality", icon: Zap },
  { key: "fusion", label: "Perform CTF multimodal fusion", detail: "Aligned features combine through attention and residual connections", icon: Brain },
  { key: "predict", label: "Generate calibrated prediction", detail: "Disease probabilities and explainable evidence are produced", icon: Cpu },
];

const DEMO_RESULT = {
  disease: "Pulmonary Tuberculosis",
  confidence: 0.94,
  modelConfidenceLabel: "HIGH MODEL CONFIDENCE",
  contributions: { Radiology: 0.38, Pathology: 0.25, Clinical: 0.22, Acoustic: 0.15 },
  prototypes: { TB: 0.94, Pneumonia: 0.04, COPD: 0.01, Normal: 0.01 },
  evidence: {
    Radiology: ["Cavitary lesion", "Upper-lobe opacity", "Hilar lymphadenopathy"],
    Pathology: ["Acid-fast bacilli", "Granulomatous inflammation"],
    Clinical: ["Persistent cough", "Night sweats", "Hemoptysis"],
    Acoustic: ["Crackles", "Reduced breath sounds", "Bronchial breathing"],
  },
  explanation: "Radiology evidence showing an upper-lobe cavitary pattern provides the strongest support. Pathology findings consistent with granulomatous inflammation and acid-fast bacilli reinforce the result. Clinical symptoms and acoustic respiratory findings add supporting evidence, while pneumonia and COPD receive substantially lower prototype similarity scores.",
  recommendation: "Consider confirmatory microbiological testing with GeneXpert and sputum culture, subject to clinician review.",
};

const initialFileStates = () =>
  MODALITIES.reduce((acc, modality) => {
    acc[modality.key] = { status: "idle", progress: 0, fileName: null, size: null };
    return acc;
  }, {});

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

function SimulatedFileDialog({ modality, selectedFile, phase }) {
  if (!modality) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
          <div>
            <p className="text-sm font-semibold text-neutral-800">Open</p>
            <p className="text-xs text-neutral-400">Demo Datasets / {modality.folder}</p>
          </div>
          <X size={18} className="text-neutral-400" />
        </div>

        <div className="grid min-h-[330px] grid-cols-[180px_1fr]">
          <aside className="border-r border-neutral-200 bg-neutral-50 p-3">
            {["Desktop", "Documents", "Downloads", "Demo Datasets"].map((folder) => (
              <div key={folder} className={`mb-1 flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${folder === "Demo Datasets" ? "bg-[#EAF6F1] font-semibold text-[#085041]" : "text-neutral-500"}`}>
                <Folder size={15} />
                {folder}
              </div>
            ))}
          </aside>

          <main className="p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-700">
              <Folder size={18} style={{ color: TEAL_MID }} />
              {modality.folder}
            </div>

            <div className="overflow-hidden rounded-lg border border-neutral-200">
              {modality.files.map((fileName, index) => {
                const highlighted = phase === "selected" && fileName === selectedFile;
                const Icon = modality.icon;
                return (
                  <div key={fileName} className={`flex items-center justify-between border-b border-neutral-100 px-3 py-3 text-xs last:border-b-0 ${highlighted ? "bg-blue-100 text-blue-900" : "text-neutral-600"}`}>
                    <div className="flex items-center gap-2">
                      <Icon size={16} style={{ color: highlighted ? "#1D4ED8" : TEAL_MID }} />
                      <span>{fileName}</span>
                    </div>
                    <span className="text-neutral-400">{index === 0 ? "Today" : "Yesterday"}</span>
                  </div>
                );
              })}
            </div>
          </main>
        </div>

        <div className="flex justify-end gap-2 border-t border-neutral-200 bg-neutral-50 px-5 py-3">
          <button className="rounded-lg border border-neutral-300 px-4 py-2 text-xs text-neutral-600">Cancel</button>
          <button className={`rounded-lg px-5 py-2 text-xs font-semibold text-white transition-all ${phase === "selected" ? "scale-[1.03]" : "opacity-70"}`} style={{ backgroundColor: TEAL_DARK }}>Open</button>
        </div>

        <div className="pointer-events-none absolute z-10" style={{ animation: phase === "selected" ? "dialogCursorOpen 0.8s ease-in-out forwards" : "dialogCursorSelect 0.9s ease-in-out forwards", color: "#111827" }}>
          <MousePointer2 size={26} fill="white" />
        </div>
      </div>
    </div>
  );
}

function UploadCard({ modality, state, focused }) {
  const Icon = modality.icon;
  const active = focused || state.status !== "idle";

  const borderColor =
    state.status === "ready"
      ? TEAL_LIGHT
      : state.status === "validating"
        ? WARNING
        : active
          ? TEAL_MID
          : "#D9DEDC";

  const backgroundColor =
    state.status === "ready"
      ? TEAL_TINT
      : state.status === "validating"
        ? WARNING_BG
        : active
          ? "#F3FAF7"
          : "transparent";

  const isCardInteraction =
    state.status === "hovering" || state.status === "clicking";

  return (
    <div
      className={`relative mb-4 transition-all duration-300 ${
        focused ? "scale-[1.015]" : ""
      } ${state.status === "clicking" ? "scale-[0.99]" : ""}`}
    >
      <div className="mb-2 flex items-center gap-2">
        <Icon size={15} style={{ color: TEAL_DARK }} />
        <span className="text-[13px] font-medium" style={{ color: TEAL_DARK }}>
          {modality.label}
        </span>

        {state.status === "hovering" && (
          <span
            className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold"
            style={{ color: TEAL_MID, backgroundColor: TEAL_TINT }}
          >
            HOVER
          </span>
        )}

        {state.status === "clicking" && (
          <span
            className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold"
            style={{ color: TEAL_DARK, backgroundColor: "#D4EDE6" }}
          >
            CLICK
          </span>
        )}

        {state.status === "ready" && (
          <span
            className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold"
            style={{ color: TEAL_MID, backgroundColor: SUCCESS_BG }}
          >
            READY
          </span>
        )}
      </div>

      <div
        className={`relative flex min-h-[116px] w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border-2 border-dashed px-4 py-4 transition-all duration-300 ${
          state.status === "selecting" || isCardInteraction ? "border-pulse" : ""
        }`}
        style={{ borderColor, backgroundColor }}
      >
        {state.status === "idle" && (
          <>
            <Upload size={21} className="text-neutral-400" />
            <span className="text-[12px] text-neutral-500">
              Waiting for demo...
            </span>
            <span className="text-center text-[10px] text-neutral-400">
              {modality.description}
            </span>
          </>
        )}

        {state.status === "hovering" && (
          <>
            <Icon size={25} style={{ color: TEAL_MID }} />
            <span className="text-[12px] font-semibold text-neutral-700">
              {modality.shortLabel} AI
            </span>
            <span className="text-[10px] text-neutral-500">
              Click to provide {modality.folder}
            </span>
          </>
        )}

        {state.status === "clicking" && (
          <>
            <Icon
              size={25}
              className="animate-[cardClickPop_0.4s_ease-out]"
              style={{ color: TEAL_DARK }}
            />
            <span className="text-[12px] font-semibold" style={{ color: TEAL_DARK }}>
              Opening {modality.folder}...
            </span>
          </>
        )}

        {state.status === "selecting" && (
          <>
            <Icon
              size={25}
              className="animate-[fileMove_0.7s_ease-in-out_infinite]"
              style={{ color: TEAL_MID }}
            />
            <span className="text-[12px] font-medium text-neutral-700">
              Opening file browser...
            </span>
          </>
        )}

        {state.status === "uploading" && (
          <>
            <Icon
              size={24}
              className="animate-bounce"
              style={{ color: TEAL_MID }}
            />
            <span className="max-w-full truncate text-[12px] font-medium text-neutral-700">
              {state.fileName}
            </span>
            <div className="w-full max-w-[220px]">
              <div className="mb-1 flex justify-between text-[10px]">
                <span className="text-neutral-500">Uploading</span>
                <span className="font-semibold" style={{ color: TEAL_MID }}>
                  {state.progress}%
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-neutral-200">
                <div
                  className="h-full rounded-full transition-all duration-150"
                  style={{
                    width: `${state.progress}%`,
                    backgroundColor: TEAL_MID,
                  }}
                />
              </div>
            </div>
          </>
        )}

        {state.status === "validating" && (
          <>
            <Loader2
              size={24}
              className="animate-spin"
              style={{ color: WARNING }}
            />
            <span className="max-w-full truncate text-[12px] font-medium text-neutral-700">
              {state.fileName}
            </span>
            <span className="text-[10px] text-neutral-500">
              Validating file type and modality...
            </span>
          </>
        )}

        {state.status === "ready" && (
          <>
            <CheckCircle2
              size={27}
              className="animate-[readyPop_0.45s_ease-out]"
              style={{ color: TEAL_MID }}
            />
            <span className="max-w-full truncate text-[12px] font-medium text-neutral-700">
              {state.fileName}
            </span>
            <span className="text-[10px] text-neutral-400">{state.size}</span>
            <span className="text-[11px] font-semibold" style={{ color: TEAL_MID }}>
              Ready for fusion
            </span>
          </>
        )}

        {isCardInteraction && (
          <div
            className="pointer-events-none absolute z-20"
            style={{
              animation:
                state.status === "clicking"
                  ? "modalityCursorClick 0.42s ease-in-out forwards"
                  : "modalityCursorMove 0.65s ease-out forwards",
              color: "#111827",
            }}
          >
            <MousePointer2 size={28} fill="white" />
          </div>
        )}
      </div>
    </div>
  );
}

function PipelineStep({ step, status }) {
  const Icon = step.icon;
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all duration-300" style={{ backgroundColor: status === "pending" ? RULE : TEAL_DARK, boxShadow: status === "active" ? `0 0 0 5px ${TEAL_TINT}` : "none" }}>
        {status === "active" ? <Loader2 size={15} className="animate-spin text-white" /> : status === "done" ? <CheckCircle2 size={15} className="text-white" /> : <Icon size={14} className="text-neutral-400" />}
      </div>
      <div className="pb-5">
        <p className="text-[13px] font-semibold" style={{ color: status === "pending" ? SOFT : "#1F2A28" }}>{step.label}</p>
        <p className="mt-0.5 text-[11px] text-neutral-400">{step.detail}</p>
      </div>
    </div>
  );
}

function AnimatedBar({ label, value, color }) {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setDisplayValue(value));
    return () => window.cancelAnimationFrame(frame);
  }, [value]);

  return (
    <div className="mb-3">
      <div className="mb-1 flex justify-between text-[12px]"><span className="font-medium text-neutral-700">{label}</span><span className="text-neutral-500">{Math.round(displayValue * 100)}%</span></div>
      <div className="h-2 overflow-hidden rounded-full bg-neutral-100"><div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${Math.min(displayValue * 100, 100)}%`, backgroundColor: color }} /></div>
    </div>
  );
}

export default function AnimatedDemo() {
  const [fileStates, setFileStates] = useState(initialFileStates);
  const [focusedModality, setFocusedModality] = useState(null);
  const [dialogModality, setDialogModality] = useState(null);
  const [dialogPhase, setDialogPhase] = useState("opening");
  const [stage, setStage] = useState("idle");
  const [activeStep, setActiveStep] = useState(-1);
  const [running, setRunning] = useState(false);
  const [runButtonPulse, setRunButtonPulse] = useState(false);
  const [reveals, setReveals] = useState({ prediction: false, contributions: false, prototypes: false, evidence: false, explanation: false, recommendation: false });

  const runIdRef = useRef(0);
  const explanationRef = useRef(null);
  const recommendationRef = useRef(null);

  const reset = () => {
    runIdRef.current += 1;
    setFileStates(initialFileStates());
    setFocusedModality(null);
    setDialogModality(null);
    setDialogPhase("opening");
    setStage("idle");
    setActiveStep(-1);
    setRunning(false);
    setRunButtonPulse(false);
    setReveals({ prediction: false, contributions: false, prototypes: false, evidence: false, explanation: false, recommendation: false });
  };

  useEffect(() => () => { runIdRef.current += 1; }, []);
  const validRun = (id) => runIdRef.current === id;

  const simulateModality = async (modality, runId) => {
    setFocusedModality(modality.key);

    setFileStates((prev) => ({
      ...prev,
      [modality.key]: {
        ...prev[modality.key],
        status: "hovering",
      },
    }));
    await sleep(CARD_HOVER_MS);
    if (!validRun(runId)) return false;

    setFileStates((prev) => ({
      ...prev,
      [modality.key]: {
        ...prev[modality.key],
        status: "clicking",
      },
    }));
    await sleep(CARD_CLICK_MS);
    if (!validRun(runId)) return false;

    setFileStates((prev) => ({
      ...prev,
      [modality.key]: {
        ...prev[modality.key],
        status: "selecting",
      },
    }));
    setDialogModality(modality);
    setDialogPhase("opening");
    await sleep(FILE_DIALOG_MS);
    if (!validRun(runId)) return false;

    setDialogPhase("selected");
    await sleep(FILE_SELECT_MS);
    if (!validRun(runId)) return false;

    setDialogModality(null);
    setFileStates((prev) => ({ ...prev, [modality.key]: { status: "uploading", progress: 0, fileName: modality.selectedFile, size: modality.size } }));
    for (let progress = 10; progress <= 100; progress += 10) {
      await sleep(UPLOAD_TICK_MS);
      if (!validRun(runId)) return false;
      setFileStates((prev) => ({ ...prev, [modality.key]: { ...prev[modality.key], progress } }));
    }

    setFileStates((prev) => ({ ...prev, [modality.key]: { ...prev[modality.key], status: "validating" } }));
    await sleep(VALIDATION_MS);
    if (!validRun(runId)) return false;

    setFileStates((prev) => ({ ...prev, [modality.key]: { ...prev[modality.key], status: "ready" } }));
    await sleep(READY_PAUSE_MS);
    return validRun(runId);
  };

  const startDemo = async () => {
    if (running) return;
    reset();
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    setRunning(true);
    setStage("patient-loading");

    await sleep(700);
    if (!validRun(runId)) return;
    setStage("uploading");

    for (const modality of MODALITIES) {
      const completed = await simulateModality(modality, runId);
      if (!completed) return;
    }

    setFocusedModality(null);
    setStage("run-button");
    setRunButtonPulse(true);
    await sleep(1100);
    if (!validRun(runId)) return;

    setRunButtonPulse(false);
    setStage("pipeline");
    for (let index = 0; index < PIPELINE_STEPS.length; index += 1) {
      setActiveStep(index);
      await sleep(PIPELINE_STEP_MS);
      if (!validRun(runId)) return;
    }

    setActiveStep(PIPELINE_STEPS.length);
    await sleep(450);
    if (!validRun(runId)) return;

    setStage("results");
    setReveals((prev) => ({ ...prev, prediction: true }));
    await sleep(900);
    if (!validRun(runId)) return;
    setReveals((prev) => ({ ...prev, contributions: true }));
    await sleep(900);
    if (!validRun(runId)) return;
    setReveals((prev) => ({ ...prev, prototypes: true }));
    await sleep(900);
    if (!validRun(runId)) return;
    setReveals((prev) => ({ ...prev, evidence: true }));
    await sleep(1100);
    if (!validRun(runId)) return;
    setReveals((prev) => ({ ...prev, explanation: true }));
    await sleep(100);
    explanationRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    await sleep(1400);
    if (!validRun(runId)) return;
    setReveals((prev) => ({ ...prev, recommendation: true }));
    await sleep(100);
    recommendationRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setStage("complete");
    setRunning(false);
  };

  const readyCount = Object.values(fileStates).filter((state) => state.status === "ready").length;
  const buttonLabel = { idle: "Start Animated Demo", "patient-loading": "Loading Patient Case...", uploading: "Preparing Demo Files...", "run-button": "Run Fusion Demo", pipeline: "Running CTF Fusion...", results: "Generating Explainable Results...", complete: "Replay Animated Demo" }[stage];

  return (
    <div className="w-full bg-[#F7F8F7] p-4 font-sans sm:p-6">
      <style>{`
        @keyframes fileMove { 0% { transform: translateY(-8px); opacity: .45; } 50% { transform: translateY(3px); opacity: 1; } 100% { transform: translateY(-8px); opacity: .45; } }
        @keyframes readyPop { 0% { transform: scale(.6); opacity: 0; } 70% { transform: scale(1.15); opacity: 1; } 100% { transform: scale(1); } }
        @keyframes borderPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(15,110,86,0); } 50% { box-shadow: 0 0 0 7px rgba(15,110,86,.14); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes dialogCursorSelect { 0% { opacity: 0; left: 74%; top: 78%; } 30% { opacity: 1; left: 62%; top: 45%; } 70% { opacity: 1; left: 62%; top: 45%; transform: scale(.85); } 100% { opacity: 1; left: 62%; top: 45%; transform: scale(1); } }
        @keyframes dialogCursorOpen { 0% { opacity: 1; left: 62%; top: 45%; } 65% { opacity: 1; left: 87%; top: 91%; } 82% { opacity: 1; left: 87%; top: 91%; transform: scale(.82); } 100% { opacity: 0; left: 87%; top: 91%; transform: scale(1); } }

        @keyframes modalityCursorMove {
          0% { opacity: 0; right: -28px; bottom: -18px; transform: scale(1); }
          35% { opacity: 1; }
          100% { opacity: 1; right: 42%; bottom: 34%; transform: scale(1); }
        }
        @keyframes modalityCursorClick {
          0% { opacity: 1; right: 42%; bottom: 34%; transform: scale(1); }
          45% { opacity: 1; right: 42%; bottom: 34%; transform: scale(.76); }
          78% { opacity: 1; right: 42%; bottom: 34%; transform: scale(1); }
          100% { opacity: 0; right: 42%; bottom: 34%; transform: scale(1); }
        }
        @keyframes cardClickPop {
          0% { transform: scale(1); }
          45% { transform: scale(.88); }
          100% { transform: scale(1); }
        }
        @keyframes runCursorClick { 0% { opacity: 0; transform: translate(55px,30px); } 35% { opacity: 1; transform: translate(4px,2px); } 58% { opacity: 1; transform: translate(4px,2px) scale(.8); } 80% { opacity: 1; transform: translate(4px,2px) scale(1); } 100% { opacity: 0; transform: translate(4px,2px); } }
        .border-pulse { animation: borderPulse 1.1s ease-in-out infinite; }
        .slide-up { animation: slideUp .55s ease-out forwards; }
      `}</style>

      {dialogModality && <SimulatedFileDialog modality={dialogModality} selectedFile={dialogModality.selectedFile} phase={dialogPhase} />}

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
        <section className="rounded-2xl bg-white p-6 shadow-[0_2px_20px_-4px_rgba(8,80,65,0.12)]">
          <div className="mb-4 flex items-center justify-between">
            <div><h2 className="text-[15px] font-bold" style={{ color: TEAL_DARK }}>Patient Case TB-2026-0001</h2><p className="text-[10px] text-neutral-400">Initial screening workflow</p></div>
            <span className="rounded-full bg-[#EAF6F1] px-2 py-1 text-[10px] font-semibold text-[#085041]">GUIDED</span>
          </div>

          {MODALITIES.map((modality) => <UploadCard key={modality.key} modality={modality} state={fileStates[modality.key]} focused={focusedModality === modality.key} />)}

          <div className="relative mt-2 flex gap-2">
            <button onClick={startDemo} disabled={running} className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-3 text-[13px] font-semibold text-white transition-all duration-300 disabled:cursor-wait disabled:opacity-60 ${runButtonPulse ? "scale-[1.025] animate-pulse" : ""}`} style={{ backgroundColor: TEAL_DARK }}>
              {running && stage !== "run-button" ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} fill="white" />}
              {buttonLabel}
            </button>
            <button onClick={reset} className="flex items-center gap-2 rounded-lg border px-4 py-3 text-[13px] font-semibold hover:bg-[#EAF6F1]" style={{ color: TEAL_DARK, borderColor: TEAL_LIGHT }}><RotateCcw size={15} />Reset</button>
            {stage === "run-button" && <div className="pointer-events-none absolute left-1/2 top-1/2 z-10" style={{ animation: "runCursorClick 1s ease-in-out forwards", color: "#111827" }}><MousePointer2 size={27} fill="white" /></div>}
          </div>
        </section>

        <section className="max-h-[760px] min-h-[620px] overflow-y-auto rounded-2xl bg-white p-6 shadow-[0_2px_20px_-4px_rgba(8,80,65,0.12)] sm:p-8">
          {stage === "idle" && <div className="flex min-h-[540px] flex-col items-center justify-center text-center"><div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: TEAL_TINT }}><Play size={23} style={{ color: TEAL_MID }} /></div><h3 className="text-lg font-semibold" style={{ color: TEAL_DARK }}>CTF Guided Demonstration</h3><p className="mt-2 max-w-sm text-sm text-neutral-500">Watch the platform simulate file selection, ingestion, multimodal fusion and explainable AI.</p><p className="mt-3 text-xs text-neutral-400">No real patient files are used.</p></div>}
          {stage === "patient-loading" && <div className="flex min-h-[540px] flex-col items-center justify-center text-center"><Loader2 size={36} className="mb-4 animate-spin" style={{ color: TEAL_MID }} /><h3 className="text-lg font-semibold" style={{ color: TEAL_DARK }}>Loading Patient Case</h3><p className="mt-2 text-sm text-neutral-500">Preparing case TB-2026-0001 for multimodal analysis</p></div>}
          {stage === "uploading" && <div className="flex min-h-[540px] flex-col items-center justify-center text-center"><Upload size={38} className="mb-4 animate-bounce" style={{ color: TEAL_MID }} /><h3 className="text-lg font-semibold" style={{ color: TEAL_DARK }}>Preparing Multimodal Patient Case</h3><p className="mt-2 text-sm text-neutral-500">Selecting, uploading and validating demonstration files</p><div className="mt-6 w-full max-w-sm"><div className="mb-2 flex justify-between text-xs text-neutral-500"><span>Modalities ready</span><span>{readyCount} / {MODALITIES.length}</span></div><div className="h-2 overflow-hidden rounded-full bg-neutral-100"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${(readyCount / MODALITIES.length) * 100}%`, backgroundColor: TEAL_MID }} /></div></div></div>}
          {stage === "run-button" && <div className="flex min-h-[540px] flex-col items-center justify-center text-center"><CheckCircle2 size={44} className="mb-4" style={{ color: TEAL_MID }} /><h3 className="text-lg font-semibold" style={{ color: TEAL_DARK }}>Patient Case Ready</h3><p className="mt-2 text-sm text-neutral-500">Launching the CTF Fusion Core</p></div>}
          {stage === "pipeline" && <div className="mx-auto max-w-xl py-3"><span className="mb-6 inline-block rounded-full px-3 py-1 text-[10px] font-semibold tracking-wide" style={{ backgroundColor: TEAL_TINT, color: TEAL_DARK }}>CTF PIPELINE</span>{PIPELINE_STEPS.map((step, index) => <PipelineStep key={step.key} step={step} status={index < activeStep ? "done" : index === activeStep ? "active" : "pending"} />)}</div>}

          {(stage === "results" || stage === "complete") && reveals.prediction && <div>
            <div className="slide-up mb-6"><p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400">AI Primary Verdict</p><div className="mt-2 flex flex-wrap items-end justify-between gap-3"><h3 className="text-2xl font-bold" style={{ color: TEAL_DARK }}>{DEMO_RESULT.disease}</h3><div className="text-right"><p className="text-lg font-bold" style={{ color: TEAL_MID }}>{Math.round(DEMO_RESULT.confidence * 100)}%</p><p className="text-[10px] font-semibold" style={{ color: TEAL_MID }}>{DEMO_RESULT.modelConfidenceLabel}</p></div></div></div>
            {reveals.contributions && <div className="slide-up mb-6"><h4 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">Modality Contribution</h4><div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">{Object.entries(DEMO_RESULT.contributions).map(([label, value]) => <AnimatedBar key={label} label={label} value={value} color={label === "Radiology" ? TEAL_DARK : label === "Pathology" ? "#7C3AED" : label === "Clinical" ? TEAL_MID : TEAL_LIGHT} />)}</div></div>}
            {reveals.prototypes && <div className="slide-up mb-6"><h4 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">Disease Prototype Similarity</h4><div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">{Object.entries(DEMO_RESULT.prototypes).map(([label, value]) => <AnimatedBar key={label} label={label} value={value} color={TEAL_MID} />)}</div></div>}
            {reveals.evidence && <div className="slide-up mb-6"><h4 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">Explainable Evidence</h4><div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{Object.entries(DEMO_RESULT.evidence).map(([modality, items]) => <div key={modality} className="rounded-xl border border-[#EDEFEE] bg-[#FAFBFA] p-4"><p className="mb-2 text-xs font-semibold text-neutral-700">{modality}</p>{items.map((item) => <div key={item} className="mb-1.5 flex items-center gap-2 text-xs text-neutral-600"><CheckCircle2 size={13} style={{ color: TEAL_MID }} />{item}</div>)}</div>)}</div></div>}
            {reveals.explanation && <div ref={explanationRef} className="slide-up mb-5 rounded-xl border p-4" style={{ borderColor: TEAL_LIGHT, backgroundColor: TEAL_TINT }}><h4 className="text-sm font-semibold" style={{ color: TEAL_DARK }}>Why did the AI choose Tuberculosis?</h4><p className="mt-2 text-[13px] leading-relaxed text-neutral-700">{DEMO_RESULT.explanation}</p></div>}
            {reveals.recommendation && <div ref={recommendationRef} className="slide-up"><div className="rounded-xl border border-[#EDEFEE] bg-white p-4"><div className="flex items-start gap-2"><Info size={16} className="mt-0.5" style={{ color: TEAL_MID }} /><div><p className="text-xs font-semibold" style={{ color: TEAL_DARK }}>Illustrative Next Step</p><p className="mt-1 text-[13px] text-neutral-600">{DEMO_RESULT.recommendation}</p></div></div></div><p className="mt-4 border-t border-[#EDEFEE] pt-3 text-center text-[10px] text-neutral-400">Demonstration only. Not intended for clinical diagnosis or treatment.</p></div>}
          </div>}
        </section>
      </div>
    </div>
  );
}
